import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DocItem } from '@/mocks/documents';

interface DocAccordionProps {
  items: DocItem[];
}

export default function DocAccordion({ items }: DocAccordionProps) {
  const [openId, setOpenId] = useState<string | null>(items[0]?.id ?? null);
  const { i18n } = useTranslation();
  const lang = i18n.language || 'kk';

  const isEn = lang.startsWith('en');
  const isRu = lang.startsWith('ru');

  const toggle = (id: string) => {
    setOpenId((prev) => (prev === id ? null : id));
  };

  return (
    <div className="space-y-2">
      {items.map((item) => {
        const isOpen = openId === item.id;
        const displayTitle = isEn
          ? item.titleEn || item.title
          : isRu
          ? item.titleRu || item.title
          : item.titleKk || item.title;

        return (
          <div
            key={item.id}
            className="border border-background-800/60 rounded-lg overflow-hidden"
          >
            <button
              onClick={() => toggle(item.id)}
              className="w-full flex items-start gap-4 px-6 py-5 text-left bg-background-900/80 hover:bg-background-800/80 transition-colors duration-200 cursor-pointer"
            >
              <span
                className={`mt-0.5 w-6 h-6 flex items-center justify-center flex-shrink-0 rounded border transition-colors duration-200 ${
                  isOpen
                    ? 'border-accent-500 text-accent-500'
                    : 'border-foreground-600 text-foreground-500'
                }`}
              >
                <i className={`ri-${isOpen ? 'subtract-line' : 'add-line'} text-sm`} />
              </span>
              <span className="text-sm font-label text-foreground-300 leading-relaxed tracking-wide">
                {displayTitle}
              </span>
            </button>

            <div
              className={`overflow-hidden transition-all duration-300 ${
                isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="px-6 py-4 bg-background-950 border-t border-background-800/40">
                <ul className="space-y-2">
                  {item.files.map((file, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-accent-500">
                        <i className="ri-file-pdf-line text-base" />
                      </span>
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-accent-400 hover:text-accent-300 underline underline-offset-2 transition-colors duration-200 break-all"
                      >
                        {file.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}